import { toError } from "./errors";
/**
 * A promise that waits some number of ms before resolving
 *
 * @returns Promise of setTimeout handle (for cancelling the timeout)
 */
export async function wait(ms) {
    return new Promise((resolve) => {
        const handle = setTimeout(() => {
            resolve(handle);
        }, ms);
    });
}
/**
 * Given a promise, returns a wrapped promise that will reject if the given timeout is hit before the original promise resolves.
 *
 * Note that promises cannot be cancelled, so the original promise will continue to run even after the timeout is hit.
 */
export async function timeout(promiseToTimeout, timeoutInMs, timeoutMessage = `Timeout after ${timeoutInMs}ms`) {
    return new Promise((resolve, reject) => {
        const handle = setTimeout(() => {
            reject(new Error(timeoutMessage));
        }, timeoutInMs);
        promiseToTimeout.then((res) => {
            clearTimeout(handle);
            resolve(res);
        }, (error) => {
            clearTimeout(handle);
            reject(error);
        });
    });
}
export function isPromiseRejectedResult(result) {
    return result.status === "rejected";
}
export async function allSettledOrThrow(promises, logger, aggregateErrorMessage) {
    const results = await Promise.allSettled(promises);
    const rejected = results.filter((isPromiseRejectedResult));
    if (rejected.length > 0) {
        if (rejected.length === 1) {
            throw toError(rejected.pop()?.reason);
        }
        // Although we're about to throw an AggregateError, which is built-in to ES2021+ (and should be understood by
        // many tools), practically, when this error appears (e.g. in a test) we'll only get the aggregate error message
        // and not the individual errors. So we'll log the individual errors here, too.
        for (const result of rejected) {
            logger.error(result.reason);
        }
        throw new AggregateError(rejected.map((result) => result.reason), aggregateErrorMessage);
    }
}
/**
 * A simple queue that processes tasks in order, with concurrency of 1.
 */
export class PromiseQueue {
    queue = [];
    running = false;
    async add(task) {
        return new Promise((resolve, reject) => {
            const wrappedTask = async () => {
                try {
                    resolve(await task());
                }
                catch (error) {
                    reject(error);
                }
            };
            this.queue.push(wrappedTask);
            this.processNext();
        });
    }
    reset() {
        this.queue = [];
        this.running = false;
    }
    async processNext() {
        if (this.running)
            return;
        const task = this.queue.shift();
        if (!task)
            return;
        this.running = true;
        try {
            await task();
        }
        finally {
            this.running = false;
            this.processNext();
        }
    }
}
/**
 * A simple mutex implementation that allows for async locking.
 *
 * Usage:
 * const mutex = new AsyncMutex();
 * const release = await mutex.acquire();
 * // do some work
 * release();
 */
export class AsyncMutex {
    queue = [];
    locked = false;
    async acquire() {
        return new Promise((resolve) => {
            const release = () => {
                this.locked = false;
                if (this.queue.length > 0) {
                    const nextResolve = this.queue.shift();
                    if (nextResolve)
                        nextResolve();
                }
            };
            if (this.locked) {
                this.queue.push(release);
            }
            else {
                this.locked = true;
                resolve(release);
            }
        });
    }
}
//# sourceMappingURL=promise.js.map